import Constants from 'expo-constants';
import { CalyxClient } from '@platform/http/calyx-client';
import { partnerSupplier, runtimeEnvironment } from '@/utils/environment';
import { useQuery } from '@tanstack/react-query';

export interface MicroProduct {
  /** Sprout Kit icon name. Mutually exclusive with displayIconUrl. */
  displayIconName: string | null;
  displayIconUrl: string | null;
  displayName: string;
  id: string;
  props: Record<string, unknown>;
}

export interface MicroProductSlot {
  products: MicroProduct[];
  slot: string;
}
interface MicroProductDto {
  display_icon_name?: string | null;
  display_icon_url?: string | null;
  display_name: string;
  id: string;
  props: Record<string, unknown> | null;
}

interface MicroProductSlotDto {
  products?: MicroProductDto[] | null;
  slot: string;
}

interface MicroProductsConfigDto {
  slots: MicroProductSlotDto[];
}

const transformMicroProduct = (dto: MicroProductDto): MicroProduct => ({
  displayIconName: dto.display_icon_name ?? null,
  displayIconUrl: dto.display_icon_url ?? null,
  displayName: dto.display_name,
  id: dto.id,
  props: dto.props ?? {},
});

// This transform runs over every slot in the response, including slots this
// client version doesn't know about — a malformed or product-less slot must
// degrade to empty rather than reject the whole query.
const transformMicroProductSlot = (
  dto: MicroProductSlotDto,
): MicroProductSlot => ({
  products: (dto.products ?? []).map(transformMicroProduct),
  slot: dto.slot,
});

/**
 * Available slots for microproducts configuration.
 * Each slot represents a location in the UI where microproducts can be rendered.
 */
export enum MicroProductsSlot {
  /** Bottom-left experience switcher — whole experiences, not pages. */
  EXPERIENCE_SWITCHER = 'experience-switcher',
  /** Finance Hub left-nav — products scoped to the finance domain. */
  FINANCE_HUB_SIDEBAR = 'finance-hub-sidebar',
  /** Sales-portal pricing sub-navigation — products scoped to the pricing domain. */
  PRICING_SIDEBAR = 'pricing-sidebar',
  SALES_HUB_SIDEBAR = 'sales-hub-sidebar',
  /** Operator-portal (restaurant-facing) sidebar — the 'storefront' surface. */
  STOREFRONT_SIDEBAR = 'storefront-sidebar',
}

export const buildMicroProductsPath = (
  businessOrganizationUUID: string,
  restaurantUUID?: string,
  appVersion?: string,
) => {
  const basePath = `/business-organizations/${businessOrganizationUUID}/microproducts/config`;
  const query: string[] = [];
  if (restaurantUUID) {
    query.push(`restaurant_id=${encodeURIComponent(restaurantUUID)}`);
  }
  if (appVersion) {
    query.push(`app_version=${encodeURIComponent(appVersion)}`);
  }
  return query.length > 0 ? `${basePath}?${query.join('&')}` : basePath;
};

export interface UseMicroProductsLoaderOptions {
  /** Set false to skip the fetch entirely (e.g. no restaurant selected yet).
   *  Defaults to true. */
  enabled?: boolean;
  /**
   * Restaurant context, sent as the `restaurant_id` query param. Required for
   * restaurant-membership (operator) users — Calyx resolves their access via
   * this restaurant and returns empty slots without it. Supplier-membership
   * (sales hub) callers omit it.
   */
  restaurantUUID?: string;
}

interface UseMicroProductsLoaderResult {
  error: Error | null;
  loading: boolean;
  slots: MicroProductSlot[];
}

/**
 * Shared loader for microproducts configuration.
 * Fetches all microproducts slots from the API.
 *
 * Uses React Query for caching - multiple consumers share the same cached data.
 *
 * @example
 * const { slots } = useMicroProductsLoader();
 * const sidebarProducts = slots.find(s => s.slot === MicroProductsSlot.SALES_HUB_SIDEBAR)?.products;
 */
export const useMicroProductsLoader = (
  options?: UseMicroProductsLoaderOptions,
): UseMicroProductsLoaderResult => {
  const businessOrganizationUUID = partnerSupplier.businessOrganizationUUID;
  const restaurantUUID = options?.restaurantUUID;

  // Only production has a real app version, so we'll require that staging
  // have all microproducts enabled for all versions
  const appVersion =
    runtimeEnvironment === 'production'
      ? Constants.expoConfig?.version
      : undefined;

  const { data, error, isLoading } = useQuery({
    enabled: options?.enabled ?? true,
    queryFn: async ({ signal }) => {
      const response = await CalyxClient.get<MicroProductsConfigDto>(
        buildMicroProductsPath(
          businessOrganizationUUID,
          restaurantUUID,
          appVersion,
        ),
        { signal },
      );
      return {
        slots: response.slots.map(transformMicroProductSlot),
      };
    },
    queryKey: [
      'microproducts-config',
      businessOrganizationUUID,
      restaurantUUID ?? null,
      appVersion ?? null,
    ],
  });

  return {
    error: error ?? null,
    loading: isLoading,
    slots: data?.slots ?? [],
  };
};
